import { For, Show, type JSX } from "solid-js";
import type { PowerNow } from "@energyplatform/client";
import { locale, t } from "../i18n.ts";
import { formatNumber } from "../format.ts";
import { houseState, type HouseFlow } from "../home.ts";
import { Icon, type IconName } from "../icons.tsx";

const kw = (v: number | null) =>
  v === null ? "—" : `${formatNumber(v, locale(), v < 10 ? 2 : 1)} kW`;

// Where the nodes are: the grid on the left, the home on the right, PV above and the battery below the line between.
const GRID = { x: 70, y: 130 };
const HOME = { x: 430, y: 130 };
const PV = { x: 250, y: 46 };
const BESS = { x: 250, y: 214 };
const R = 38;

/**
 * Power now as an energy flow: circles for the grid, PV, the home battery and the home, joined by lines whose dots
 * move the way the energy goes (still when nothing flows), and beside it where the home's power goes — the largest
 * measured loads and the rest. Values come from the balance the host calculates.
 */
export function EnergyFlow(props: { now: PowerNow }) {
  const s = () => houseState(props.now, 6);
  return (
    <Show
      when={s()}
      fallback={<p class="hint">{t("places.house.noBoundary")}</p>}
    >
      {(st) => {
        const exporting = () => st().grid.direction === "out";
        const shareOf = (v: number) =>
          (st().use ?? 0) > 0 ? Math.min(1, v / st().use!) : 0;
        return (
          <div class="eflow">
            <svg
              class="eflow-svg"
              viewBox="0 0 500 260"
              role="img"
              aria-label={t("places.house.label", {
                use: kw(st().use),
                grid: kw(st().grid.kw),
              })}
            >
              <Link
                d={`M${GRID.x + R},${GRID.y} H${HOME.x - R - 6}`}
                flow={st().grid}
                kind={exporting() ? "export" : "grid"}
              />
              <Show when={st().pv}>
                {(pv) => (
                  <Link
                    d={`M${PV.x},${PV.y + R} C${PV.x},${HOME.y - 12} ${HOME.x - 120},${HOME.y - 12} ${HOME.x - R - 4},${HOME.y - 10}`}
                    flow={pv()}
                    kind="pv"
                  />
                )}
              </Show>
              <Show when={st().battery}>
                {(b) => (
                  <Link
                    d={`M${BESS.x},${BESS.y - R} C${BESS.x},${HOME.y + 12} ${HOME.x - 120},${HOME.y + 12} ${HOME.x - R - 4},${HOME.y + 10}`}
                    flow={b()}
                    kind="bess"
                  />
                )}
              </Show>
              <Node
                at={GRID}
                icon="bolt"
                kind={exporting() ? "export" : "grid"}
                value={kw(st().grid.kw)}
                label={
                  exporting()
                    ? t("places.flow.toGrid")
                    : st().grid.direction === "idle"
                      ? t("places.flow.grid")
                      : t("places.flow.fromGrid")
                }
              />
              <Show when={st().pv}>
                {(pv) => (
                  <Node
                    at={PV}
                    icon="sun"
                    kind="pv"
                    value={kw(pv().kw)}
                    label={t("places.flow.pv")}
                  />
                )}
              </Show>
              <Show when={st().battery}>
                {(b) => (
                  <Node
                    at={BESS}
                    icon="battery"
                    kind="bess"
                    value={kw(b().kw)}
                    label={
                      b().direction === "out"
                        ? t("places.house.charging")
                        : b().direction === "in"
                          ? t("places.house.discharging")
                          : t("places.house.batteryIdle")
                    }
                  />
                )}
              </Show>
              <Node
                at={HOME}
                icon="home"
                kind="home"
                value={kw(st().use)}
                label={t("places.flow.home")}
                big
              />
            </svg>
            <div class="eflow-loads">
              <h3>{t("places.flow.where")}</h3>
              <Show
                when={st().loads.length > 0}
                fallback={<p class="hint">{t("places.flow.noLoads")}</p>}
              >
                <For each={st().loads}>
                  {(l) => (
                    <LoadRow name={l.name} kw={l.kw} share={shareOf(l.kw)} />
                  )}
                </For>
                <Show when={st().rest !== null && st().rest! > 0.01}>
                  <LoadRow
                    name={t("places.flow.rest")}
                    kw={st().rest!}
                    share={shareOf(st().rest!)}
                    rest
                  />
                </Show>
              </Show>
            </div>
          </div>
        );
      }}
    </Show>
  );
}

function LoadRow(props: {
  name: string;
  kw: number;
  share: number;
  rest?: boolean;
}): JSX.Element {
  return (
    <div class={`eflow-load${props.rest ? " rest" : ""}`} title={props.name}>
      <span class="nm">{props.name}</span>
      <span class="v num">{kw(props.kw)}</span>
      <span class="bar">
        <span style={{ width: `${Math.max(2, props.share * 100)}%` }} />
      </span>
    </div>
  );
}

function Node(props: {
  at: { x: number; y: number };
  icon: IconName;
  kind: string;
  value: string;
  label: string;
  big?: boolean;
}) {
  const r = () => (props.big ? R + 6 : R);
  return (
    <g class={`eflow-node ${props.kind}`}>
      <circle cx={props.at.x} cy={props.at.y} r={r()} />
      <svg
        x={props.at.x - 10}
        y={props.at.y - r() + 10}
        width="20"
        height="20"
        viewBox="0 0 24 24"
      >
        <Icon name={props.icon} />
      </svg>
      <text
        x={props.at.x}
        y={props.at.y + 8}
        text-anchor="middle"
        class={`val${props.big ? " big" : ""}`}
      >
        {props.value}
      </text>
      <text
        x={props.at.x}
        y={props.at.y + r() + 16}
        text-anchor="middle"
        class="lbl"
      >
        {props.label}
      </text>
    </g>
  );
}

/** A line between a node and the home; its dots run toward the home ("in") or away from it ("out"). */
function Link(props: { d: string; flow: HouseFlow; kind: string }) {
  const moving = () =>
    props.flow.direction === "idle"
      ? "idle"
      : props.flow.direction === "in"
        ? "forward"
        : "backward";
  return (
    <>
      <path d={props.d} class="eflow-track" />
      <path d={props.d} class={`eflow-dots ${props.kind} ${moving()}`} />
    </>
  );
}
